<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { Pagination, Subscription } from '@/types'
  import type { GetSubscriptionForm } from '@/types/forms/Subscription.ts'
  import { useContentHeight } from '@/composables/useContentHeight.ts'
  import { usePaymentDialog } from '@/composables/usePaymentDialog.ts'

  definePage({
    meta: {
      authenticated: true,
    },
  })

  const { t, locale } = useI18n()
  const router = useRouter()
  const { show: showPaymentDialog } = usePaymentDialog()

  // Reactive state
  const subscriptions = ref<Pagination<Subscription> | null>(null)
  const loading = ref(false)
  const error = ref('')
  const currentPage = ref(1)
  const perPage = ref(4)

  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionsStore()

  const { subscription: activeSubscription } = storeToRefs(subscriptionStore)

  const fetchSubscriptions = async (filter: GetSubscriptionForm['filter'] = {}) => {
    loading.value = true
    error.value = ''

    try {
      const { data } = await subscriptionStore.fetchSubscriptions({
        filter,
        page: currentPage.value,
        per_page: perPage.value,
        include: 'user,plan,planPaymentMethod',
      })

      perPage.value = data.per_page
      currentPage.value = data.current_page
      subscriptions.value = data
    } catch (err) {
      return err
    } finally {
      loading.value = false
    }
  }

  const handlePageChange = (page: number) => {
    currentPage.value = page
    fetchSubscriptions()
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'غير محدد'
    return new Date(dateString).toLocaleDateString(locale.value === 'ar' ? `ar-${window.CURRENT_COUNTRY_ISO2}` : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const loadActiveSubscription = async () => {
    try {
      if (!activeSubscription.value?.id) {
        return
      }

      const { data } = await subscriptionStore.fetchSubscriptions({
        filter: {
          id: activeSubscription.value?.id,
        },
        include: 'user,plan,planPaymentMethod',
      })

      if (data.data.length > 0) {
        activeSubscription.value = data.data[0]
      } else {
        activeSubscription.value = null
      }
    } catch (err) {
      console.error('Error fetching active subscription:', err)
    }
  }

  const { availableHeight } = useContentHeight()

  onMounted(async () => {
    await loadActiveSubscription()

    const filter: GetSubscriptionForm['filter'] = {}
    if (activeSubscription.value?.id) {
      filter.exclude_ids = [activeSubscription.value.id]
    }

    await fetchSubscriptions(filter)
  })
</script>

<template>
  <v-container
    class="pa-0 mt-md-8 overflow-hidden subscription-list"
    :fluid="$vuetify.display.smAndDown"
    height="100%"
    :max-height="availableHeight"
  >
    <v-card
      class="d-flex flex-column mx-auto py-7 px-4 overflow-auto"
      elevation="0"
      height="100%"
      max-width="540"
    >

      <app-back-btn
        v-if="!$vuetify.display.smAndDown"
        :title="t('subscription.my_subscription')"
        @back="router.back()"
      />

      <!-- Error Alert -->
      <v-alert
        v-if="error"
        class="mb-4"
        closable
        type="error"
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <!-- Loading State with Skeletons -->
      <template v-if="loading">
        <!-- Current/Active Subscription Section Title Skeleton -->
        <v-skeleton-loader
          class="mb-3"
          height="20"
          type="text"
          width="200"
        />

        <!-- Current/Active Subscription Card Skeleton -->
        <div class="mb-8">
          <v-skeleton-loader
            class="rounded-md border-primary-sm"
            height="400"
            type="card"
            width="100%"
          >
            <template #default>
              <div class="pa-4 bg-highlight-background w-100">
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1 me-4">
                    <!-- Plan name skeleton -->
                    <v-skeleton-loader
                      class="mb-2"
                      height="20"
                      type="heading"
                    />
                    <!-- Date info skeleton -->
                    <v-skeleton-loader
                      class="mb-1"
                      height="16"
                      type="text"
                      width="180"
                    />
                    <!-- Subscription ID skeleton -->
                    <v-skeleton-loader
                      height="16"
                      type="text"
                      width="100"
                    />
                  </div>
                  <!-- Action button skeleton -->
                  <v-skeleton-loader
                    height="32"
                    type="button"
                    width="100"
                  />
                </div>
              </div>
            </template>
          </v-skeleton-loader>
        </div>

        <!-- Previous Subscriptions Section Title Skeleton -->
        <v-skeleton-loader
          class="mb-3"
          height="20"
          type="text"
          width="150"
        />

        <!-- Previous Subscriptions List Skeleton -->
        <div class="d-flex flex-column ga-3">
          <v-skeleton-loader
            v-for="n in 4"
            :key="n"
            class="bg-grey-800 rounded-md"
            height="100"
            type="card"
          >
            <template #default>
              <div class="pa-4">
                <!-- Plan name skeleton -->
                <v-skeleton-loader
                  class="mb-2"
                  height="20"
                  type="heading"
                  width="160"
                />
                <div class="d-flex align-center justify-space-between">
                  <!-- Date info skeleton -->
                  <v-skeleton-loader
                    height="16"
                    type="text"
                    width="140"
                  />
                  <!-- Subscription ID skeleton -->
                  <v-skeleton-loader
                    height="16"
                    type="text"
                    width="80"
                  />
                </div>
              </div>
            </template>
          </v-skeleton-loader>
        </div>

        <!-- Pagination Skeleton -->
        <div class="d-flex justify-center mt-auto pt-4">
          <v-skeleton-loader
            height="40"
            type="button"
            width="200"
          />
        </div>
      </template>

      <!-- Loaded Content -->
      <template v-else-if="!loading">
        <!-- current subscription or subscribe now -->
        <p class="text-grey-300 text-body-2 font-weight-medium mb-3">{{ t('subscription.my_subscriptions') }}</p>
        <div class="mb-8">
          <v-card
            border="primary sm opacity-100"
            class="bg-highlight-background"
            flat
            rounded="md"
          >
            <v-card-text
              v-if="!authStore.isSubscribed"
              class="pa-4"
            >
              <div class="d-flex align-center justify-space-between ga-2">
                <div>
                  <h3 class="text-md-h6 text-body-2 font-weight-bold mb-1">
                    {{ t('subscription.subscribe_message') }}
                  </h3>
                </div>
                <v-btn
                  class="flex-0-1-100 font-weight-bold"
                  color="primary"
                  flat
                  height="32"
                  max-width="125"
                  rounded="sm"
                  @click="showPaymentDialog()"
                >
                  {{ t('subscription.subscribe_now') }}
                </v-btn>
              </div>
            </v-card-text>
            <v-card-text
              v-else-if="activeSubscription"
              class="px-4 py-6"
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <h4 class="text-md-h6 text-body-2 font-weight-bold mb-2 text-grey-100">
                    {{ activeSubscription.plan?.name || t('subscription.default_plan_name') }}</h4>
                  <p class="text-caption text-grey-200 mb-1">
                    <i18n-t v-if="activeSubscription.plan?.is_recurring" keypath="subscription.next_payment">
                      <template #date>
                        {{ formatDate(activeSubscription.next_payment) }}
                      </template>
                    </i18n-t>

                    <i18n-t v-else keypath="subscription.end_at">
                      <template #date>
                        {{ formatDate(activeSubscription.ended_at || activeSubscription.next_payment) }}
                      </template>
                    </i18n-t>
                  </p>
                  <p class="text-caption text-grey-200">
                    <i18n-t keypath="subscription.subscription_id">
                      <template #id>
                        {{ activeSubscription.id }}
                      </template>
                    </i18n-t>
                  </p>
                </div>
                <v-btn
                  class="font-weight-bold"
                  color="primary"
                  flat
                  height="32"
                  max-width="125"
                  rounded="sm"
                  :to="{ name: 'profile-subscriptions-id', params: { id: activeSubscription.id } }"
                >
                  {{ t('subscription.show_details') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Subscriptions List -->
        <h4
          v-if="subscriptions?.data && subscriptions.data.length > 0"
          class="text-grey-300 text-body-2 font-weight-medium mb-3"
        >{{ t('subscription.previous') }}</h4>
        <div v-if="subscriptions?.data && subscriptions.data.length > 0" class="d-flex flex-column ga-3">
          <v-card
            v-for="subscription in subscriptions.data"
            :key="subscription.id"
            class="bg-grey-800"
            flat
            rounded="md"
            :to="{ name: 'profile-subscriptions-id', params: { id: subscription.id } }"
          >
            <v-card-text class="pa-4">
              <h4 class="text-body-2 font-weight-bold mb-2 text-grey-100">
                {{ subscription.plan?.name || t('subscription.default_plan_name') }}</h4>

              <div class="d-flex align-center justify-space-between ga-2">
                <p class="text-caption text-grey-200">
                  <i18n-t v-if="!!subscription.ended_at" keypath="subscription.ended_at">
                    <template #date>
                      {{ formatDate(subscription.ended_at) }}
                    </template>
                  </i18n-t>

                  <i18n-t v-else keypath="subscription.will_end_at">
                    <template #date>
                      {{ formatDate(subscription.next_payment) }}
                    </template>
                  </i18n-t>
                </p>

                <p class="text-caption text-grey-200">
                  <i18n-t keypath="subscription.subscription_id">
                    <template #id>
                      {{ subscription.id }}
                    </template>
                  </i18n-t>
                </p>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </template>

      <!-- Pagination -->
      <div v-if="subscriptions && subscriptions.total > perPage && !loading" class="d-flex justify-center mt-auto">
        <v-pagination
          v-model="currentPage"
          color="primary"
          :length="subscriptions.last_page"
          :total-visible="perPage"
          @update:model-value="handlePageChange"
        />
      </div>
    </v-card>
  </v-container>
</template>

<style scoped>
.subscription-list :deep(.v-skeleton-loader) {
  overflow: hidden;
  display: flex;
  align-items: center;
  align-content: center;

  .v-skeleton-loader__bone {
    height: 50%;
  }
}
</style>
